import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import {
  Typography,
  Box,
  Grid,
  ListItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material'

import { ThemeButton, SelectInput, PageDisplayer, CardCheckList } from '../../../../ui'
import { useTariffCalls, useTariffDialogs, useZonesReducer } from '../../hooks'
import { useComunidades } from '../../../tariffs/hooks/useLoadComunidades'
import { tariffNewStyles } from './tariff-zone.style'

export const TariffZones = withStyles(tariffNewStyles)(({ classes }) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { getTariff, getTariffZone, putTariff, putTariffZone } = useTariffCalls()
  const { warnZoneUnsaved, warnZoneVisible, warnZoneChanges } = useTariffDialogs()
  const {
    comunidad,
    provincias,
    zips,
    zoneLoad,
    zoneUpdateZip,
    zoneUpdateProvincia,
    zoneUpdateFull,
    zoneUpdateFromCodes,
  } = useZonesReducer()

  const {
    comunidades: loadedComunidades,
    isLoading: isLoadingComunidades,
    error: comunidadesError,
  } = useComunidades()

  const comunidades = useMemo(() => loadedComunidades.filter((c) => c.activa), [loadedComunidades])

  const { tariffId } = useParams()

  const [empresaId, setEmpresaId] = useState(null)
  const [empresaAll, setEmpresaAll] = useState({})
  const [selectedComunidadId, setSelectedComunidadId] = useState(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const [isChanged, setIsChanged] = useState(false)
  const [provinciaMirada, setProvinciaMirada] = useState(null)
  const [searchQuery, setSearchQuery] = useState(null)
  const [isLoadingZona, setIsLoadingZona] = useState(false)

  useEffect(() => {
    getTariff(tariffId).then(({ data }) => {
      setEmpresaId(data.empresa_id)
      setEmpresaAll(data)
    })
  }, [tariffId, getTariff])

  useEffect(() => {
    if (!empresaId) return
    setIsLoadingZona(true)
    getTariffZone(empresaId, tariffId).then(({ data }) => {
      const postalGroups = data.codPostales
      setProvinciaMirada(postalGroups[0]?.prov || null)
      zoneLoad({ comunidad, codPostales: postalGroups })
      setIsChanged(false)
      const empty =
        postalGroups.reduce((sum, prov) => sum + prov.cps.filter((cp) => cp.activa).length, 0) === 0
      setIsEmpty(empty)
      setIsLoadingZona(false)
    })
  }, [empresaId, tariffId, getTariffZone, zoneLoad, comunidad])

  useEffect(() => {
    if (empresaId && selectedComunidadId === null && provincias.some((p) => p.chosen > 0)) {
      const com = comunidades.find((c) => c.empresa_ca_id === empresaId)
      if (com) setSelectedComunidadId(com.id)
    }
  }, [empresaId, selectedComunidadId, provincias, comunidades])

  const cc_aa_choice = useMemo(
    () => comunidades.map((c) => ({ key: c.id, value: c.nombre })),
    [comunidades]
  )

  //GOING BACK
  const handleGoBack = useCallback(() => {
    navigate('/tariffs/' + tariffId + '/edit')
  }, [navigate, tariffId])
  const handleCallGoBack = useCallback(() => {
    if (isChanged) warnZoneUnsaved(handleGoBack)
    else handleGoBack()
  }, [isChanged, warnZoneUnsaved, handleGoBack])

  const handleCallEmpresaChange = useCallback(
    (e) => {
      const newComId = parseInt(e.target.value, 10)
      setSelectedComunidadId(newComId)
      const com = comunidades.find((c) => c.id === newComId)
      if (!com) return
      const newEmpresaId = com.empresa_ca_id
      if (!isEmpty) {
        warnZoneChanges(comunidad, com.nombre, () => {
          setEmpresaId(newEmpresaId)
          putTariff(tariffId, { ...empresaAll, empresa_id: newEmpresaId })
          setIsEmpty(true)
        })
      } else {
        setEmpresaId(newEmpresaId)
        putTariff(tariffId, { ...empresaAll, empresa_id: newEmpresaId })
      }
    },
    [comunidades, isEmpty, comunidad, warnZoneChanges, putTariff, tariffId, empresaAll]
  )

  const handleSubmitZoneConfig = useCallback(
    (arr) => {
      setIsChanged(false)
      return putTariffZone(tariffId, { codPostales: arr }).then(() => {
        setIsEmpty(arr.length === 0)
        navigate('/tariffs')
      })
    },
    [tariffId, putTariffZone, navigate]
  )

  const handleCallSubmitZoneConfig = useCallback(() => {
    const arr = Object.values(zips)
      .flat()
      .filter((cp) => cp.activa)
      .map((cp) => cp.value)

    if (isEmpty && arr.length > 0 && empresaAll.activa) {
      warnZoneVisible(comunidad, () => handleSubmitZoneConfig(arr))
    } else {
      handleSubmitZoneConfig(arr)
    }
  }, [zips, isEmpty, empresaAll, comunidad, warnZoneVisible, handleSubmitZoneConfig])

  const handleProvinciaSelect = useCallback((prov) => {
    setSearchQuery(null)
    setProvinciaMirada(prov)
  }, [])
  const handleProvinciaCheck = useCallback(
    (e) => {
      setIsChanged(true)
      zoneUpdateProvincia(e.target.name, e.target.checked)
    },
    [zoneUpdateProvincia]
  )
  const handleAllProvinciaCheck = useCallback(
    (e) => {
      setIsChanged(true)
      zoneUpdateFull(e.target.checked)
    },
    [zoneUpdateFull]
  )
  const handleCodeCheck = useCallback(
    (e, codes) => {
      setIsChanged(true)
      zoneUpdateFromCodes(e.target.checked, codes)
    },
    [zoneUpdateFromCodes]
  )
  const handleZipChange = useCallback(
    (e) => {
      setIsChanged(true)
      zoneUpdateZip(provinciaMirada, e.target.name, e.target.checked)
    },
    [provinciaMirada, zoneUpdateZip]
  )
  const handleSearchQueryChange = useCallback((e) => {
    setSearchQuery(e.target.value)
  }, [])

  const provinceCheckRender = useCallback(
    (el) => (
      <ListItem
        key={el.name}
        className={`${classes.provinciaLine}${
          el.name === provinciaMirada ? ` ${classes.provinciaLineChosen}` : ''
        }`}
        onClick={() => handleProvinciaSelect(el.name)}
      >
        <Checkbox
          color="primary"
          name={el.name}
          checked={el.chosen === el.size}
          indeterminate={el.chosen < el.size && el.chosen > 0}
          onChange={handleProvinciaCheck}
          disableRipple
        />
        {el.name}
      </ListItem>
    ),
    [provinciaMirada, classes, handleProvinciaSelect, handleProvinciaCheck]
  )

  const zip_choice = useMemo(
    () =>
      (zips[provinciaMirada] || []).filter((el) =>
        searchQuery ? el.value.includes(searchQuery) : true
      ),
    [zips, provinciaMirada, searchQuery]
  )

  const zipCheckRender = useCallback(
    (el) => (
      <FormControlLabel
        key={`${el.value}-${provinciaMirada}`}
        className={`${classes.zipCard}${el.activa ? ` ${classes.zipCardChosen}` : ''}`}
        label={el.value}
        control={
          <Checkbox
            color="primary"
            name={el.value}
            checked={el.activa}
            onChange={handleZipChange}
            disableRipple
          />
        }
      />
    ),
    [classes, handleZipChange, provinciaMirada]
  )

  if (isLoadingZona) {
    return (
      <PageDisplayer loading>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress size={60} />
        </Box>
      </PageDisplayer>
    )
  }

  return (
    <PageDisplayer
      loading={isLoadingComunidades}
      error={!!comunidadesError}
      handleGoBack={handleCallGoBack}
      textGoBack={formatMessage({ id: 'pages.tariffs.zone.back' })}
    >
      <Typography variant="h4" color="textSecondary">
        {formatMessage({ id: 'pages.tariffs.zone.title' })}
      </Typography>

      <Box mt={3}>
        <Grid container spacing={5}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {formatMessage({ id: 'pages.tariffs.zone.cc_aa' })}
            </Typography>
            <Box position="relative">
              <SelectInput
                values={cc_aa_choice}
                value={selectedComunidadId}
                onChange={handleCallEmpresaChange}
                disabled={isLoadingComunidades}
                error={!!comunidadesError}
              />
              {isLoadingComunidades && (
                <CircularProgress
                  size={28}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 'calc(100% - 40px)',
                    marginTop: -14,
                    marginLeft: -14,
                    zIndex: 2,
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid
            className={classes.buttonContainer}
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ThemeButton
              onClick={handleCallSubmitZoneConfig}
              disabled={selectedComunidadId === null}
            >
              {formatMessage({ id: 'pages.tariffs.zone.save_button' })}
            </ThemeButton>
          </Grid>
        </Grid>
      </Box>
      {selectedComunidadId !== null && empresaId && (
        <Box mt={3}>
          <Grid container spacing={5}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {formatMessage({ id: 'pages.tariffs.zone.provinces.title' })}
              </Typography>
              <CardCheckList
                className={classes.colorPanel}
                keyField="name"
                nameField="name"
                elements={provincias}
                selected={provincias.filter((p) => p.size === p.chosen).map((p) => p.name)}
                funcRenderElement={provinceCheckRender}
                wholeSelector
                wholeCaption={formatMessage({ id: 'pages.tariffs.zone.provinces.all' })}
                funcWholeOnChange={handleAllProvinciaCheck}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {formatMessage({ id: 'pages.tariffs.zone.zips.title' })}
              </Typography>
              <CardCheckList
                className={classes.colorPanel}
                keyField="value"
                nameField="value"
                elements={zip_choice}
                selected={zip_choice.filter((z) => z.activa).map((z) => z.value)}
                funcRenderElement={zipCheckRender}
                searchBox
                searchValue={searchQuery}
                searchPlaceholder={formatMessage({ id: 'pages.tariffs.zone.zips.filter' })}
                funcSearchOnChange={handleSearchQueryChange}
                wholeSelector
                wholeCaption={formatMessage({ id: 'pages.tariffs.zone.zips.all' })}
                funcWholeOnChange={handleCodeCheck}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </PageDisplayer>
  )
})
